// @ts-check
import "common/components/custom-range-slider";
import { h } from "common/dom-builder";
import { smallIconButton } from "common/icon-buttons";
import { isPresent } from "common/utils/blank";
import { preferences } from "../preferences";
import {
  getSituationalMaxScaledFontSize,
  maxNominalFontSize,
  maxScaledFontSize,
  minNominalFontSize,
  minScaledFontSize,
} from "./font-size-basics";
import { debug } from "debug";
import { max, min } from "common/utils/number-limits";
import { logMaxFontSize } from "store/javascripts/utils/debug-loggers";
import { logGroup } from "store/javascripts/utils/logging";

/**
 * @typedef {Object} Props
 * @prop {import("../plugins/text-length-limiter-plugin").TypeTester} typeTester
 * @prop {HTMLElement} [element]
 * @prop {any} [panel]
 */

/**
 * Font size slider functionality for TypeTester.
 *
 * It generates a slider element for the DOM, sets the value in TypeTester when
 * the slider is adjusted, and updates the state of the slider when the font size in other places,
 * e.g. when the font-scaler updates the font-size when it's too large.
 *
 * We distinguish between scaled and nominal font size.
 *
 * Scaled font size:
 * - is the value that is actually set in the DOM
 * - this will change when the window is resized
 * - shown to the user next to the slider
 * - rounded to the nearest integer to avoid displaying fractional values to the user
 *
 * Nominal font size:
 * - reference value defined for the sample for 1000px window width
 * - scaled proportionally based on the window width
 * - not rounded, so that the scaled font size does not change due to rounding errors when the number of columns changes
 *
 * Updates from Type-Tester's font-size changes come via font-scaler through updateFromScaling.
 *
 */
export class FontSizeSelector {
  parent;
  element;

  inputElement;
  scalingFactor = 1;

  /** @type {Props} */
  props;

  /**
   * @param {Props} props
   */
  constructor(props) {
    this.log = debug("type-tester:font-size-selector");
    this.element = props.element ?? props.panel?.panelElement;
    this.props = props;
    this.init();
  }

  /** @type {import("../types").ToolBuildMethod<FontSizeSelector>} */
  static buildElementForHeader(typeTester) {
    if (!typeTester.isFormattable) return;

    if (preferences.showFontSizePanelInHeader) {
      const element = h(
        "div",
        {
          class: ["input-group", "size-input-group", "in-header"],
          "data-input-group": "size",
        },
        []
      );
      const instance = new FontSizeSelector({ typeTester, element });
      return {
        element,
        instance,
      };
    } else {
      const element = h(
        "div",
        {
          class: [
            "input-group",
            "size-input-group",
            "expandable",
            // "expand-with-pushdown", // feature panel is the only one pushing down
          ],
          "data-input-group": "size",
        },
        [
          h("span", { class: "label" }, "Size"),
          h("span", { class: "icon-button expand-button" }),
        ]
      );
      return { element };
    }
  }

  get initialFontSize() {
    return this.props.typeTester.props.format.fontSize;
  }

  get hasMaxHeight() {
    return isPresent(this.props.typeTester.props.options.maxHeight);
  }

  init() {
    logGroup("FontSizeSelector.init", () => {
      this.render();

      // Update textLength when the editor is loaded & text is edited,
      // so that the max font size can be adjusted based on the text length.
      this.props.typeTester?.addEventListener("change", (event) => {
        const detail = event.detail;
        if (detail.reason === "text-change") {
          const textLength = detail.textLength;
          logMaxFontSize(
            `FontSizeSelector: text-change event: textLength: ${textLength}`
          );
          this.updateFromTextChange({ textLength: detail.textLength });
        }
      });

      if (this.props.typeTester?.textEditor) {
        const textEditor = this.props.typeTester.textEditor;
        if (textEditor.quill) {
          const textLength = textEditor.textLength;
          logMaxFontSize(`FontSizeSelector.init: textLength: ${textLength}`);
          this.updateFromTextChange({ textLength });
        } else {
          textEditor.addEventListener("quill-editor-initialized", () => {
            const textLength = textEditor.textLength;
            logMaxFontSize(
              `FontSizeSelector quill-editor-initialized: textLength: ${textLength}`
            );
            this.updateFromTextChange({ textLength });
          });
        }
      }
    });
  }

  render() {
    this.element.appendChild(
      h(
        "div",
        {
          class: ["with-icon-in-front", "with-value-indicator", "tool-panel"],
        },
        [
          smallIconButton("size"),
          this.buildInput(),
          this.renderValueIndicator(),
        ]
      )
    );
    this.setValueFromNominalFontSize(this.initialFontSize);
    this.updateTypeTester();
    this.updateIndicator();
  }

  updateTypeTester() {
    this.props.typeTester.textEditor.setFontSize(this.nominalValue);
  }

  updateIndicator() {
    this.valueIndicatorElement.textContent = `${this.scaledValue}`;
  }

  updateInput() {
    this.inputElement.value = this.scaledValue;
  }

  buildInput() {
    const inputElement = (this.inputElement = h("custom-range-slider", {
      min: minScaledFontSize,
      max: maxScaledFontSize ?? maxNominalFontSize,
      step: 1,
      scale: "log",
      value: this.initialFontSize,
    }));
    inputElement.addEventListener("input", (ev) => {
      this.setValueFromScaledFontSize(ev.target.value);
      this.updateTypeTester();
      this.updateIndicator();
    });
    inputElement.addEventListener("change", (ev) => {
      this.setValueFromScaledFontSize(ev.target.value);
      this.updateTypeTester();
      this.updateIndicator();
      // Notify about user interaction so that we can move the focus back to the text editor
      this.props.typeTester.element.dispatchEvent(
        new CustomEvent("interaction")
      );
    });
    this.props.typeTester.addEventListener("reset", () => {
      this.setValueFromNominalFontSize(this.initialFontSize);
      this.updateInput();
      this.updateIndicator();
    });
    this.inputElement = inputElement;
    return inputElement;
  }

  renderValueIndicator() {
    return (this.valueIndicatorElement = h(
      "span",
      {
        class: ["value-indicator", "tabular-numbers"],
        style: `--value-indicator-max-digits: ${Math.max(
          Math.ceil(maxScaledFontSize).toString().length,
          Math.floor(minScaledFontSize).toString().length
        )};`,
      },
      [`${this.initialFontSize}`]
    ));
  }

  /**
   * @param {Object} props
   * @param {number} props.scalingFactor
   * @param {number} props.scaledFontSize
   * @param {number} props.columnCount
   * @param {boolean} props.columnsDeterminedAutomatically
   */
  updateFromScaling({
    scalingFactor,
    scaledFontSize,
    columnCount,
    columnsDeterminedAutomatically,
  }) {
    logGroup("FontSizeSelector.updateFromScaling", () => {
      this.log(
        `updateFromScaling (columnsDeterminedAutomatically: ${columnsDeterminedAutomatically})`
      );
      this.scalingFactor = scalingFactor;
      this.columnCount = columnCount;
      this.columnsDeterminedAutomatically = columnsDeterminedAutomatically;

      if (!this.inputElement) {
        return;
      }

      this.updateMinMaxValues();

      this.setValueFromScaledFontSize(scaledFontSize);
      this.updateInput();
      this.updateIndicator();
      this.updateTypeTester();
    });
  }

  updateFromTextChange({ textLength }) {
    this.textLength = textLength;
    this.updateMinMaxValues();
  }

  updateMinMaxValues() {
    const minFontSize = max(
      minScaledFontSize,
      minNominalFontSize * this.scalingFactor
    );
    const maxFontSize = min(
      maxScaledFontSize,
      maxNominalFontSize * this.scalingFactor,
      getSituationalMaxScaledFontSize({
        columnCount: this.columnCount,
        textLength: this.textLength,
        columnsDeterminedAutomatically: this.columnsDeterminedAutomatically,
      })
    );
    logMaxFontSize(
      `Updating font size slider range to ${minFontSize} - ${maxFontSize} (column count: ${
        this.columnCount
      }, text length: ${this.textLength}, ${
        this.columnsDeterminedAutomatically
          ? "columns automatically determined"
          : "columns set manually"
      })`
    );
    this.inputElement.setAttribute("min", minFontSize);
    this.inputElement.setAttribute("max", maxFontSize);
  }

  setValueFromScaledFontSize(scaledFontSize) {
    this.scaledValue = Math.round(scaledFontSize);
    this.nominalValue = scaledFontSize / this.scalingFactor;
    this.log(
      "Set value from scaled font size:",
      this.scaledValue,
      this.nominalValue
    );
  }

  // Called during initialization and when resetting to the defaults
  setValueFromNominalFontSize(nominalFontSize) {
    this.scaledValue = Math.round(nominalFontSize * this.scalingFactor);
    this.nominalValue = nominalFontSize;
    this.log(
      "Set value from nominal font size:",
      this.scaledValue,
      this.nominalValue
    );
  }
}
